<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { i18n, keycloak } from "../../main.ts";
import AccountSettingsView from "../account/AccountSettingsView.vue";
import UserMenu from "@persolog/ui/UserMenu.vue";
import { AssociatedAccount, Language, Trainer } from "../../types/PersologOnlineTypes.ts";
import PurchaseView from "../account/PurchaseView.vue";
import {
    changeLanguage,
    getDisplayName,
    getImpersonatedAccount,
    isNewAccount,
    refreshPage,
} from "../../utils/GeneralUtils.ts";
import DistributorView from "../account/DistributorView.vue";
import LinkAccountView from "../account/LinkAccountView.vue";

const firstName = computed(() => keycloak.tokenParsed?.given_name || "");
const registrationUrl = ref<string | null>(null);

const { t } = useI18n();
const personalAccountSettings = ref(false);
const linkAccountView = ref(false);
const purchaseOverview = ref(false);
const distributorOverview = ref(false);
// Linked account the user is acting in (impersonation), null for the own account.
const account = ref<AssociatedAccount | null>(null);

const props = defineProps({
    trainer: {
        type: Object as () => Trainer | null,
        default: null,
    },
});

const portrait = ref(props.trainer?.portrait);
const trainer = ref();

const itemsLoggedOut = reactive([
    {
        label: computed(() => t("header.login")),
        icon: "pi pi-sign-in",
        command: () => {
            keycloak.login({ redirectUri: globalThis.location.origin + "/products" });
        },
    },
    {
        label: computed(() => t("header.register")),
        icon: "pi pi-user-plus",
        url: computed(() => registrationUrl.value ?? undefined),
    },
]);

const languages = ref(
    i18n.global.availableLocales.map((lang) => ({
        label: getDisplayName(lang, lang),
        selected: computed(() => i18n.global.locale.value.toUpperCase() === lang.toUpperCase()),
        command: () => changeLanguage(lang as Language),
    })),
);
const separator = { separator: true };
const personalAccountItem = {
    label: computed(() => t("header.accountSettings.personalAccount")),
    icon: "pi pi-id-card",
    command: () => {
        personalAccountSettings.value = true;
    },
};
const linkAccountItem = {
    label: computed(() => t("header.accountSettings.accountLinks.heading")),
    icon: "pi pi-link",
    command: () => {
        linkAccountView.value = true;
    },
};
const purchaseOverviewItem = {
    label: computed(() => t("header.accountSettings.transactions")),
    icon: "pi pi-chart-bar",
    command: () => {
        purchaseOverview.value = true;
    },
};
const distributorOverviewItem = {
    label: computed(() => t("header.accountSettings.distributor.heading-sub-accounts"),),
    icon: "pi pi-sitemap",
    command: () => {
        distributorOverview.value = true;
    },
};
const logoutItem = {
    label: computed(() => t("header.logout")),
    icon: "pi pi-sign-out",
    command: () => {
        localStorage.removeItem("newAccount");
        // Shared with profile-download (same origin); a leftover key survives logout otherwise.
        localStorage.removeItem("impersonatedAccount");
        keycloak.logout({ redirectUri: globalThis.location.origin + "/" });
    },
};
const languageItem = {
    label: computed(() => t("header.changeLanguage")),
    icon: "pi pi-globe",
    items: languages.value,
};

const manualItem = {
    label: computed(() => t("footer.persologWorldMenu.help")),
    icon: "pi pi-book",
    command: () => {
        openManual();
    },
};

function openManual() {
    const url = new URL(globalThis.location.href);
    url.searchParams.set("search", "");
    globalThis.location.href = url.href;
}

const distributorSectionLabelItem = {
    label: computed(() => t("header.accountSettings.distributor.menu-section")),
    disabled: true,
    style: "font-size: 0.6rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; padding-top: 0.2rem; padding-bottom: 0.01rem; cursor: default;",
};

const itemsLoggedInDistributor = reactive([
    personalAccountItem,
    linkAccountItem,
    separator,
    distributorSectionLabelItem,
    purchaseOverviewItem,
    distributorOverviewItem,
    separator,
    manualItem,
    languageItem,
    separator,
    logoutItem,
]);

const itemsLoggedIn = reactive([
    personalAccountItem,
    linkAccountItem,
    separator,
    purchaseOverviewItem,
    separator,
    manualItem,
    languageItem,
    separator,
    logoutItem,
]);

const endImpersonationItem = {
    label: computed(() =>
        t("header.accountSettings.impersonation.end", {
            firstname: account.value?.associatedAccount?.firstname,
            lastname: account.value?.associatedAccount?.lastname,
        }),
    ),
    icon: "pi pi-sign-out",
    command: () => {
        localStorage.removeItem("impersonatedAccount");
        refreshPage();
    },
};

// While acting in a linked account, the avatar shows that account, so the own-account
// items (personal settings, links, transactions, sub-accounts) would act on a different
// account than the one pictured: only neutral items stay, and logout becomes "end".
const itemsImpersonating = reactive([manualItem, languageItem, separator, endImpersonationItem]);

const menuModel = computed(() => {
    if (!keycloak.authenticated) {
        return itemsLoggedOut;
    }
    if (account.value) {
        return itemsImpersonating;
    }
    return trainer.value?.isDistributorAccount ? itemsLoggedInDistributor : itemsLoggedIn;
});

const impersonationTitle = computed(() =>
    account.value
        ? t("header.accountSettings.impersonation.title", {
              firstname: account.value.associatedAccount.firstname,
              lastname: account.value.associatedAccount.lastname,
          })
        : "",
);

function displayAccountSettings(show: boolean) {
    personalAccountSettings.value = show;
}

function displayLinkedAccountView(show: boolean) {
    linkAccountView.value = show;
}

function displayProfilePurchases(show: boolean) {
    purchaseOverview.value = show;
}

function displayDistributorOverview(show: boolean) {
    distributorOverview.value = show;
}

function updatePicture(picture: string) {
    portrait.value = picture;
    // Keep the trainer object in sync — the header avatar's png fallback reads
    // trainer.portrait, so an explicit deletion/replacement would otherwise be
    // masked by the stale stored value until a page reload.
    if (trainer.value) {
        trainer.value = { ...trainer.value, portrait: picture };
    }
}

// Every view works on the trainer loaded at boot (main.ts trainerDetails), so switching
// between the own and a linked account reloads the page, whichever view it starts from.
function updateImpersonatedAccount() {
    refreshPage();
}

watch(
    () => props.trainer,
    () => {
        portrait.value = props.trainer?.portrait;
        // Keep the local trainer (used by the avatar and the account dialogs) in sync with
        // refreshed trainer details after a save, so a changed/removed picture or logo is
        // reflected without a page reload. Skip while an impersonated account is active.
        if (!account.value) {
            trainer.value = props.trainer;
        }
    },
);

/**
 * Show the account settings if the user is a new account
 */
onMounted(async () => {
    if (keycloak.authenticated) {
        account.value = getImpersonatedAccount();
        personalAccountSettings.value = isNewAccount();
    }
    registrationUrl.value = await keycloak.createRegisterUrl({ redirectUri: globalThis.location.origin + "/products" });
    if (account.value) {
        trainer.value = account.value.trainer
    } else {
        trainer.value = props.trainer;
    }
});
</script>

<template>
    <UserMenu
        :authenticated="!!keycloak.authenticated"
        :trainer="account ? account.associatedAccount : ((trainer as Trainer) ?? null)"
        :portrait="account ? (account.associatedAccount.portrait ?? '') : (portrait ?? '')"
        :first-name="firstName"
        :items="menuModel"
        :badge="account ? t('header.accountSettings.impersonation.badge') : ''"
        :title="impersonationTitle"
    />
    <AccountSettingsView
        v-if="trainer"
        :trainer="trainer as Trainer"
        :visible-toggle="personalAccountSettings"
        @update:visible="displayAccountSettings"
        @update:picture="updatePicture"
    />
    <LinkAccountView
        v-if="trainer"
        :trainer="trainer as Trainer"
        :visible-toggle="linkAccountView"
        @update:visible="displayLinkedAccountView"
        @update:impersonated="updateImpersonatedAccount"
    />
    <PurchaseView
        v-if="trainer && purchaseOverview"
        :trainer="trainer as Trainer"
        :visible-toggle="purchaseOverview"
        @update:visible="displayProfilePurchases"
    />
    <DistributorView
        v-if="trainer && trainer?.isDistributorAccount"
        :trainer="trainer as Trainer"
        :visible-toggle="distributorOverview"
        @update:visible="displayDistributorOverview"
    />
</template>
